import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { useMessages } from '../../components/Messages';
import { API_CONFIG, getApiUrl, apiFetch } from '../../config/api';
import { showApiError } from '../../utils/errorUtils';
import { useTaskTypes } from '../../contexts/TaskTypesContext';
import './Workflows.css';
import { formatAppDate } from '../../utils/dateUtils';

const ViewWorkflow = forwardRef(({ workflowId, onBack, editMode = false, createMode = false, onSave, onCancel, onEdit }, ref) => {
  const { t } = useTranslation('production');
  const { t: tCommon } = useTranslation('common');
  const { getTaskTypeOptions } = useTaskTypes();
  const [workflow, setWorkflow] = useState(null);
  const [editData, setEditData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('general');
  const [hasChanges, setHasChanges] = useState(false);
  const [workflowTasks, setWorkflowTasks] = useState([]);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [taskFormData, setTaskFormData] = useState({});
  const [departments, setDepartments] = useState([]);

  const { showSuccess, showError, showWarning } = useMessages();

  useImperativeHandle(ref, () => ({
    handleDelete,
    handleSave
  }));

  useEffect(() => {
    fetchDepartments();
    if (workflowId && !createMode) {
      fetchWorkflow();
      fetchWorkflowTasks();
    } else if (createMode) {
      const newWorkflow = {
        workflow_name: '',
        workflow_description: '',
        is_active: true
      };
      setWorkflow(newWorkflow);
      setEditData(newWorkflow);
      setWorkflowTasks([]);
    }
  }, [workflowId, createMode]);

  // Add escape key handler
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Don't trigger if user is typing in an input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') {
        return;
      }

      // Don't trigger if modal is open
      if (showTaskModal) {
        return;
      }

      if (e.key === 'Escape') {
        e.preventDefault();
        // If in edit/create mode, act like cancel; otherwise go back to list
        if (editMode || createMode) {
          handleCancel();
        } else {
          if (onBack) onBack();
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [editMode, createMode, showTaskModal, onBack]);

  const fetchDepartments = async () => {
    try {
      const response = await apiFetch(getApiUrl('/api/workflows/departments'));
      if (response.ok) {
        const data = await response.json();
        setDepartments(data || []);
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const fetchWorkflow = async () => {
    setLoading(true);
    try {
      const response = await apiFetch(getApiUrl(`/api/workflows/${workflowId}`));
      if (response.ok) {
        const data = await response.json();
        setWorkflow(data);
        setEditData(data);
      } else {
        setError(t('workflows.view.error.fetchFailed'));
      }
    } catch (error) {
      console.error('Error fetching workflow:', error);
      setError(t('workflows.view.error.loadingError'));
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkflowTasks = async () => {
    try {
      const response = await apiFetch(getApiUrl(`/api/workflows/${workflowId}/tasks`));
      if (response.ok) {
        const data = await response.json();
        setWorkflowTasks(data.tasks || data || []);
      }
    } catch (error) {
      console.error('Error fetching workflow tasks:', error);
    }
  };

  const handleFieldChange = (field, value) => {
    setEditData(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      const url = createMode
        ? getApiUrl('/api/workflows/')
        : getApiUrl(`/api/workflows/${workflowId}`);

      const method = createMode ? 'POST' : 'PUT';

      const submitData = {
        workflow_name: editData.workflow_name,
        workflow_description: editData.workflow_description,
        is_active: editData.is_active
      };

      const response = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submitData)
      });

      if (response.ok) {
        const savedData = await response.json();

        // In createMode, save any pending tasks that were added before saving
        if (createMode && workflowTasks.length > 0) {
          const newWorkflowId = savedData.workflow_id;
          for (const task of workflowTasks) {
            await apiFetch(getApiUrl(`/api/workflows/${newWorkflowId}/tasks`), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(task)
            });
          }
        }

        setWorkflow(savedData);
        setEditData(savedData);
        setHasChanges(false);

        showSuccess(
          createMode ? t('workflows.view.messages.workflowCreated') : t('workflows.view.messages.workflowUpdated'),
          createMode ? t('workflows.view.messages.workflowCreatedSuccess') : t('workflows.view.messages.workflowUpdatedSuccess')
        );

        if (onSave) onSave(savedData);
      } else {
        await showApiError(response, showError, tCommon);
      }
    } catch (error) {
      console.error('Error saving workflow:', error);
      showError(tCommon('errors.genericTitle'), 'Error saving workflow');
    }
  };

  const handleCancel = () => {
    if (createMode) {
      if (onCancel) onCancel();
    } else {
      setEditData(workflow);
      setHasChanges(false);
      if (onCancel) onCancel();
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t('workflows.view.messages.deleteConfirmation'))) {
      return;
    }

    try {
      const response = await apiFetch(getApiUrl(`/api/workflows/${workflowId}`), {
        method: 'DELETE'
      });

      if (response.ok) {
        showSuccess(t('workflows.view.messages.workflowDeleted'), t('workflows.view.messages.workflowDeletedSuccess'));
        if (onBack) onBack();
      } else {
        await showApiError(response, showError, tCommon);
      }
    } catch (error) {
      console.error('Error deleting workflow:', error);
      showError(tCommon('errors.genericTitle'), 'Error deleting workflow');
    }
  };

  // Task management functions
  const handleAddTask = () => {
    setEditingTask(null);
    setTaskFormData({
      task_name: '',
      task_description: '',
      task_sequence: workflowTasks.length + 1,
      estimated_duration_minutes: 0,
      department_id: null,
      task_type: 'STANDARD'
    });
    setShowTaskModal(true);
  };

  const handleEditTask = (task) => {
    setEditingTask(task);
    setTaskFormData({
      task_name: task.task_name,
      task_description: task.task_description || '',
      task_sequence: task.task_sequence || task.sequence_order,
      estimated_duration_minutes: task.estimated_duration_minutes || 0,
      department_id: task.department_id || null,
      task_type: task.task_type || 'STANDARD'
    });
    setShowTaskModal(true);
  };

  const handleDeleteTask = async (taskId, localId) => {
    if (!window.confirm(t('workflows.view.messages.deleteTaskConfirmation'))) {
      return;
    }

    // In createMode, delete from local state
    if (createMode) {
      setWorkflowTasks(prev => prev.filter(t => t._localId !== localId));
      return;
    }

    try {
      const response = await apiFetch(getApiUrl(`/api/workflows/tasks/${taskId}`), {
        method: 'DELETE'
      });

      if (response.ok) {
        showSuccess(t('workflows.view.messages.taskDeleted'), t('workflows.view.messages.taskDeletedSuccess'));
        await fetchWorkflowTasks();
      } else {
        await showApiError(response, showError, tCommon);
      }
    } catch (error) {
      console.error('Error deleting task:', error);
      showError(tCommon('errors.genericTitle'), 'Error deleting task');
    }
  };

  const handleSaveTask = async () => {
    // In createMode, manage tasks locally — they get POSTed when the workflow is saved
    if (createMode) {
      if (editingTask) {
        setWorkflowTasks(prev => prev.map(t =>
          t._localId === editingTask._localId ? { ...taskFormData, _localId: editingTask._localId } : t
        ));
      } else {
        setWorkflowTasks(prev => [...prev, { ...taskFormData, _localId: Date.now() }]);
      }
      setShowTaskModal(false);
      return;
    }

    try {
      let url, method;

      if (editingTask) {
        url = getApiUrl(`/api/workflows/tasks/${editingTask.workflow_task_id}`);
        method = 'PUT';
      } else {
        url = getApiUrl(`/api/workflows/${workflowId}/tasks`);
        method = 'POST';
      }

      const response = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(taskFormData)
      });

      if (response.ok) {
        showSuccess(
          editingTask ? t('workflows.view.messages.taskUpdated') : t('workflows.view.messages.taskAdded'),
          editingTask ? t('workflows.view.messages.taskUpdatedSuccess') : t('workflows.view.messages.taskAddedSuccess')
        );
        setShowTaskModal(false);
        await fetchWorkflowTasks();
      } else {
        await showApiError(response, showError, tCommon);
      }
    } catch (error) {
      console.error('Error saving task:', error);
      showError(tCommon('errors.genericTitle'), 'Error saving task');
    }
  };

  const handleTaskFieldChange = (field, value) => {
    setTaskFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const renderGeneralTab = () => (
    <div className="tab-content">
      <div className="form-section">
        <h3>{t('workflows.view.sections.workflowInformation')}</h3>
        <div className="form-grid">
          <div className="form-group">
            <label>{t('workflows.view.fields.workflowName')} *</label>
            <input
              type="text"
              value={editData.workflow_name || ''}
              onChange={(e) => handleFieldChange('workflow_name', e.target.value)}
              disabled={!editMode && !createMode}
              placeholder={t('workflows.view.placeholders.workflowName')}
              required
            />
          </div>
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={editData.is_active || false}
                onChange={(e) => handleFieldChange('is_active', e.target.checked)}
                disabled={!editMode && !createMode}
              />
              <span>{t('workflows.view.fields.active')}</span>
            </label>
          </div>
          <div className="form-group full-width">
            <label>{t('workflows.view.fields.description')}</label>
            <textarea
              value={editData.workflow_description || ''}
              onChange={(e) => handleFieldChange('workflow_description', e.target.value)}
              disabled={!editMode && !createMode}
              placeholder={t('workflows.view.placeholders.workflowDescription')}
              rows="4"
            />
          </div>
        </div>
      </div>

      {!createMode && (
        <div className="form-section">
          <h3>{t('workflows.view.sections.systemInformation')}</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>{t('workflows.view.fields.createdDate')}</label>
              <input
                type="text"
                value={workflow?.created_at ? formatAppDate(workflow.created_at, 'datetime') : t('workflows.view.systemInfo.na')}
                disabled
              />
            </div>
            <div className="form-group">
              <label>{t('workflows.view.fields.lastUpdated')}</label>
              <input
                type="text"
                value={workflow?.updated_at ? formatAppDate(workflow.updated_at, 'datetime') : t('workflows.view.systemInfo.never')}
                disabled
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderTasksTab = () => (
    <div className="tab-content">
      <div className="form-section">
        <div className="section-header">
          <h3>{t('workflows.view.sections.workflowTasks')}</h3>
          <button className="btn-primary btn-sm" onClick={handleAddTask}>
            {t('workflows.view.tasks.addTask')}
          </button>
        </div>

        {workflowTasks.length === 0 ? (
          <p className="no-data">{t('workflows.view.tasks.noTasks')}</p>
        ) : (
          <div className="steps-table">
            <table>
              <thead>
                <tr>
                  <th>{t('workflows.view.tasks.headers.sequence')}</th>
                  <th>{t('workflows.view.tasks.headers.taskName')}</th>
                  <th>{t('workflows.view.tasks.headers.taskType')}</th>
                  <th>{t('workflows.view.tasks.headers.description')}</th>
                  <th>{t('workflows.view.tasks.headers.department')}</th>
                  <th>{t('workflows.view.tasks.headers.estimatedDuration')}</th>
                  <th>{t('workflows.view.tasks.headers.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {workflowTasks.sort((a, b) => (a.task_sequence || a.sequence_order || 0) - (b.task_sequence || b.sequence_order || 0)).map((task, index) => (
                  <tr key={task.workflow_task_id || index}>
                    <td className="text-center">{task.task_sequence || task.sequence_order}</td>
                    <td>{task.task_name}</td>
                    <td className="text-center">
                      <span className="task-type-badge">{task.task_type || 'STANDARD'}</span>
                    </td>
                    <td className="description">{task.task_description || t('workflows.view.tasks.defaultValues.noDescription')}</td>
                    <td>{task.department_name || t('workflows.view.tasks.defaultValues.anyDepartment')}</td>
                    <td className="text-center">{task.estimated_duration_minutes || 0} min</td>
                    <td className="actions">
                      <button
                        className="btn-icon"
                        onClick={() => handleEditTask(task)}
                        title={t('workflows.view.tasks.buttons.edit')}
                      >
                        ✏️
                      </button>
                      <button
                        className="btn-icon"
                        onClick={() => handleDeleteTask(task.workflow_task_id, task._localId)}
                        title={t('workflows.view.tasks.buttons.delete')}
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  const renderTaskModal = () => (
    <div className="modal-overlay" onClick={() => setShowTaskModal(false)}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{editingTask ? t('workflows.view.tasks.editTask') : t('workflows.view.tasks.addTask')}</h2>
          <button className="btn-close" onClick={() => setShowTaskModal(false)}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-grid">
            <div className="form-group">
              <label>{t('workflows.view.fields.taskName')} *</label>
              <input
                type="text"
                value={taskFormData.task_name || ''}
                onChange={(e) => handleTaskFieldChange('task_name', e.target.value)}
                placeholder={t('workflows.view.placeholders.taskName')}
                required
              />
            </div>
            <div className="form-group">
              <label>{t('workflows.view.fields.sequenceOrder')}</label>
              <input
                type="number"
                value={taskFormData.task_sequence || 1}
                onChange={(e) => handleTaskFieldChange('task_sequence', parseInt(e.target.value) || 1)}
                min="1"
              />
            </div>
            <div className="form-group">
              <label>{t('workflows.view.fields.taskType')}</label>
              <select
                value={taskFormData.task_type || 'STANDARD'}
                onChange={(e) => handleTaskFieldChange('task_type', e.target.value)}
              >
                {getTaskTypeOptions().map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className="form-group full-width">
              <label>{t('workflows.view.fields.taskDescription')}</label>
              <textarea
                value={taskFormData.task_description || ''}
                onChange={(e) => handleTaskFieldChange('task_description', e.target.value)}
                placeholder={t('workflows.view.placeholders.taskDescription')}
                rows="3"
              />
            </div>
            <div className="form-group">
              <label>{t('workflows.view.fields.department')}</label>
              <select
                value={taskFormData.department_id || ''}
                onChange={(e) => handleTaskFieldChange('department_id', e.target.value ? parseInt(e.target.value) : null)}
              >
                <option value="">{t('workflows.view.placeholders.anyDepartment')}</option>
                {departments.map(dept => (
                  <option key={dept.department_id} value={dept.department_id}>
                    {dept.department_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>{t('workflows.view.fields.estimatedDuration')}</label>
              <input
                type="number"
                value={taskFormData.estimated_duration_minutes || 0}
                onChange={(e) => handleTaskFieldChange('estimated_duration_minutes', parseInt(e.target.value) || 0)}
                min="0"
              />
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={() => setShowTaskModal(false)}>
            {t('workflows.view.tasks.buttons.cancel')}
          </button>
          <button className="btn-primary" onClick={handleSaveTask}>
            {editingTask ? t('workflows.view.tasks.buttons.update') : t('workflows.view.tasks.buttons.add')}
          </button>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return <div className="view-container"><div className="loading">{t('workflows.view.loading')}</div></div>;
  }

  if (error) {
    return <div className="view-container"><div className="error-message">{error}</div></div>;
  }

  if (!workflow && !createMode) {
    return <div className="view-container"><div className="loading">{t('workflows.view.titles.noWorkflowFound')}</div></div>;
  }

  return (
    <div className="view-container">
      <div className="view-header">
        <div className="header-title">
          <button className="btn-back" onClick={onBack}>←</button>
          <h1>
            {createMode ? t('workflows.view.titles.newWorkflow') : editData.workflow_name || t('workflows.view.titles.workflow')}
          </h1>
          {!createMode && editData.is_active !== undefined && (
            <span className={`status-badge ${editData.is_active ? 'status-active' : 'status-inactive'}`}>
              {editData.is_active ? t('workflows.view.status.active') : t('workflows.view.status.inactive')}
            </span>
          )}
        </div>
        <div className="header-actions">
          {!createMode && !editMode && (
            <>
              <button className="btn-secondary" onClick={onEdit}>{tCommon('ui.edit')}</button>
              <button className="btn-danger" onClick={handleDelete}>{tCommon('ui.delete')}</button>
            </>
          )}
          {(createMode || editMode) && (
            <>
              <button className="btn-secondary" onClick={handleCancel}>{tCommon('ui.cancel')}</button>
              <button className="btn-primary" onClick={handleSave}>
                {createMode ? tCommon('ui.create') : tCommon('ui.save')}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="tabs">
        <div className="tab-list">
          <button
            className={`tab-item ${activeTab === 'general' ? 'active' : ''}`}
            onClick={() => setActiveTab('general')}
          >
            {t('workflows.view.tabs.general')}
          </button>
          <button
            className={`tab-item ${activeTab === 'tasks' ? 'active' : ''}`}
            onClick={() => setActiveTab('tasks')}
          >
            {t('workflows.view.tabs.tasks')}
            {createMode && workflowTasks.length > 0 && (
              <span style={{ marginLeft: '0.4rem', background: 'var(--accent-primary)', color: '#fff', borderRadius: '10px', padding: '0 6px', fontSize: '0.75rem' }}>
                {workflowTasks.length}
              </span>
            )}
          </button>
        </div>

        <div className="tab-panels">
          {activeTab === 'general' && renderGeneralTab()}
          {activeTab === 'tasks' && renderTasksTab()}
        </div>
      </div>

      {showTaskModal && renderTaskModal()}
    </div>
  );
});

ViewWorkflow.displayName = 'ViewWorkflow';

export default ViewWorkflow;